import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../App';

// --- Material-UI Imports ---
import {
    Container, Box, Button, Paper, TextField, Typography, Stack,
    CircularProgress, Alert, Tooltip, IconButton, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Chip,
    Dialog, DialogActions, DialogContent, DialogTitle, Snackbar,
    InputAdornment, Autocomplete, Select, MenuItem, FormControl, InputLabel,
    DialogContentText, Divider
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VpnKeyIcon from '@mui/icons-material/VpnKey';

// --- INTERFACES ---
interface ApiConnection {
    id: number;
    name: string;
    api_adapter_key: string;
    api_credentials: Record<string, any>;
    api_update_mode: 'additive' | 'declarative';
    created_at: string;
    updated_at: string;
}

interface AdapterManifest {
    key: string;
    name: string;
    credentials: { key: string; label: string; type: 'text' | 'password' }[];
}

const initialFormState: Partial<ApiConnection> = {
    name: '',
    api_adapter_key: '',
    api_credentials: {},
    api_update_mode: 'additive'
};

// --- COMPONENTE PRINCIPAL ---
const ApiConnectionsPage: React.FC = () => {
    // --- ESTADOS ---
    const [connections, setConnections] = useState<ApiConnection[]>([]);
    const [adapterManifests, setAdapterManifests] = useState<AdapterManifest[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [notification, setNotification] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [editingConnection, setEditingConnection] = useState<Partial<ApiConnection> | null>(null);
    const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean, id: number | null }>({ open: false, id: null });

    // --- LÓGICA DE DATOS ---
    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [connectionsRes, manifestsRes] = await Promise.all([
                axios.get(`${API_BASE_URL}/connections`),
                axios.get(`${API_BASE_URL}/optimizer/adapter-manifests`)
            ]);
            setConnections(connectionsRes.data);
            setAdapterManifests(manifestsRes.data);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al cargar los datos.');
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // --- MANEJADORES DE EVENTOS ---
    const handleOpenForm = (connection: Partial<ApiConnection> | null = null) => {
        setEditingConnection(connection || initialFormState);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        setEditingConnection(null);
    };

    const handleSave = async (formData: Partial<ApiConnection>) => {
        const method = formData.id ? 'put' : 'post';
        const url = formData.id ? `${API_BASE_URL}/connections/${formData.id}` : `${API_BASE_URL}/connections`;

        try {
            await axios({ method, url, data: formData });
            setNotification({ type: 'success', message: `Conexión "${formData.name}" guardada con éxito.` });
            handleCloseForm();
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al guardar la conexión.' });
        }
    };

    const handleDelete = async () => {
        if (!deleteConfirm.id) return;
        try {
            await axios.delete(`${API_BASE_URL}/connections/${deleteConfirm.id}`);
            setNotification({ type: 'success', message: 'Conexión eliminada.' });
            fetchData();
        } catch (err: any) {
            setNotification({ type: 'error', message: err.response?.data?.message || 'Error al eliminar la conexión.' });
        } finally {
            setDeleteConfirm({ open: false, id: null });
        }
    };

    if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
    if (error) return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Snackbar open={!!notification} autoHideDuration={6000} onClose={() => setNotification(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setNotification(null)} severity={notification?.type} sx={{ width: '100%' }}>{notification?.message}</Alert>
            </Snackbar>

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={4}>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
                    <VpnKeyIcon sx={{ verticalAlign: 'middle', mr: 1, fontSize: '2.5rem' }} />
                    Gestor de Conexiones API
                </Typography>
                <Button variant="contained" startIcon={<AddCircleIcon />} onClick={() => handleOpenForm()}>
                    Crear Conexión
                </Button>
            </Stack>

            {isFormOpen && (
                <ConnectionForm
                    open={isFormOpen}
                    onClose={handleCloseForm}
                    onSave={handleSave}
                    connection={editingConnection}
                    adapterManifests={adapterManifests}
                />
            )}

            <ConnectionsTable
                connections={connections}
                onEdit={handleOpenForm}
                onDelete={(id) => setDeleteConfirm({ open: true, id })}
            />

            <Dialog open={deleteConfirm.open} onClose={() => setDeleteConfirm({ open: false, id: null })}>
                <DialogTitle>Confirmar Eliminación</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        ¿Estás seguro de que quieres eliminar esta conexión? Las fuentes de tráfico que la usen quedarán desconectadas.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm({ open: false, id: null })}>Cancelar</Button>
                    <Button onClick={handleDelete} color="error" variant="contained">Eliminar</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

// --- COMPONENTE DEL FORMULARIO ---
const ConnectionForm: React.FC<{
    open: boolean;
    onClose: () => void;
    onSave: (data: Partial<ApiConnection>) => void;
    connection: Partial<ApiConnection> | null;
    adapterManifests: AdapterManifest[];
}> = ({ open, onClose, onSave, connection, adapterManifests }) => {
    const [formData, setFormData] = useState<Partial<ApiConnection>>({ api_credentials: {}, api_update_mode: 'additive' });

    useEffect(() => {
        if (connection) setFormData(connection);
    }, [connection]);

    const handleChange = (field: keyof ApiConnection, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleCredentialsChange = (key: string, value: string) => {
        setFormData(prev => ({
            ...prev,
            api_credentials: {
                ...prev.api_credentials,
                [key]: value
            }
        }));
    };

    const renderCredentialFields = () => {
        const selectedAdapterManifest = adapterManifests.find(m => m.key === formData.api_adapter_key);
        if (!selectedAdapterManifest || !selectedAdapterManifest.credentials) {
            return <Typography color="text.secondary" sx={{mt: 2}}>Este adaptador no requiere credenciales.</Typography>;
        }

        return selectedAdapterManifest.credentials.map(cred => (
            <TextField
                key={cred.key}
                fullWidth
                margin="normal"
                type={cred.type || 'text'}
                label={cred.label}
                value={formData.api_credentials?.[cred.key] || ''}
                onChange={(e) => handleCredentialsChange(cred.key, e.target.value)}
            />
        ));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{connection?.id ? 'Editar Conexión' : 'Crear Nueva Conexión'}</DialogTitle>
            <DialogContent>
                <Stack spacing={3} sx={{ pt: 2 }}>
                    <TextField label="Nombre de la Conexión" value={formData.name || ''} onChange={(e) => handleChange('name', e.target.value)} fullWidth helperText="Un nombre para identificar esta conexión (ej. 'Mi Cuenta Principal de RollerAds')" />
                    <FormControl fullWidth>
                        <InputLabel>Adaptador de API</InputLabel>
                        <Select value={formData.api_adapter_key || ''} label="Adaptador de API" onChange={(e) => handleChange('api_adapter_key', e.target.value)}>
                            {adapterManifests.map(manifest => (
                                <MenuItem key={manifest.key} value={manifest.key}>{manifest.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Divider>Credenciales</Divider>
                    {renderCredentialFields()}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancelar</Button>
                <Button onClick={() => onSave(formData)} variant="contained">Guardar</Button>
            </DialogActions>
        </Dialog>
    );
};

// --- COMPONENTE DE LA TABLA ---
const ConnectionsTable: React.FC<{
    connections: ApiConnection[];
    onEdit: (connection: ApiConnection) => void;
    onDelete: (id: number) => void;
}> = ({ connections, onEdit, onDelete }) => {
    const theme = useTheme();
    const headerCellStyle = { fontWeight: 'bold', backgroundColor: alpha(theme.palette.primary.main, 0.1) };
    
    return (
        <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerCellStyle}>ID</TableCell>
                            <TableCell sx={headerCellStyle}>Nombre de la Conexión</TableCell>
                            <TableCell sx={headerCellStyle}>Adaptador</TableCell>
                            <TableCell align="right" sx={headerCellStyle}>Acciones</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {connections.map((conn) => (
                            <TableRow key={conn.id} hover>
                                <TableCell>{conn.id}</TableCell>
                                <TableCell>{conn.name}</TableCell>
                                <TableCell><Chip label={conn.api_adapter_key} size="small" /></TableCell>
                                <TableCell align="right">
                                    <Tooltip title="Editar"><IconButton onClick={() => onEdit(conn)}><EditIcon /></IconButton></Tooltip>
                                    <Tooltip title="Eliminar"><IconButton onClick={() => onDelete(conn.id)} color="error"><DeleteIcon /></IconButton></Tooltip>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};

export default ApiConnectionsPage;
